import { fromJS } from "immutable";
import { constants } from ".";
import {
  findTreeNode,
  checkIsParent,
  getNestedLineitem,
  updateDescendantLineitem,
  formatInTimeZone,
  calculateSubunitToUnit,
  sortTable,
  parseFloatValue,
} from "../../../utils/helpers";
import { calculateTotalAndGst } from "pages/makeclaim/utils/calculationHelper";
import {
  VARIATION_LINEITEM_STATUS,
  CERT_VARIATION_ACTION_TYPES,
  CLAIM_CERT_SUMMARY,
  STATUS_APPROVED,
} from "utils/constants";
import { getUpdatedVariationLineItems } from "common/lineitem/sharedComponent";
import {
  addValues,
  divideValues,
  itemQuantity,
  multiplyValues,
  roundNearestInt,
  subtractValues,
} from "utils/helpers/math";
function formatDate(date) {
  if (date) {
    return date.split("T")[0];
  } else {
    return "";
  }
}

const defaultState = fromJS({
  loading: true,
  projectlist: {},
  certificate_number: "",
  componentKey: 0,

  baseContractTable: [],
  baseCertTotal: 0,
  baseCertTotalExcludeRetention: 0,
  baseLastPeriodTotal: 0,
  baseCertList: [],
  baseThisCertTotal: 0,
  baseTotalValues: {},

  variationsTable: [],
  variationCertList: [],
  variationCertTotal: 0,
  variationCertTotalExcludeRetention: 0,
  variationLastPeriodTotal: 0,
  variationThisCertTotal: 0,
  variationsTotalValues: {},

  materialsTable: [],
  materialsCertList: [],
  materialsTotal: {
    onSite: 0,
    offSite: 0,
    onSiteClaim: 0,
    offSiteClaim: 0,
    onSiteLastPeriod: 0,
    offSiteLastPeriod: 0,
  },
  materialsTotalExcludeRetention: {
    onSite: 0,
    offSite: 0,
    onSiteClaim: 0,
    offSiteClaim: 0,
    onSiteLastPeriod: 0,
    offSiteLastPeriod: 0,
  },

  dlpReleaseValue: 0,
  pcdReleaseValue: 0,
  claimRetentionValue: {
    dlp: 0,
    pcd: 0,
  },
  certRetentionValue: {
    preNetTotal: 0,
    maxDlp: 0,
    maxPcd: 0,
  },
  retentionOption: null,

  summaryComponent: {
    previous: {},
    claimedToDate: {},
    thisCert: {},
    certToDate: {},
  },
  // previous cert ref title
  previousRef: {
    [CLAIM_CERT_SUMMARY.SUMMARY_CERTIFIED]: "",
    [CLAIM_CERT_SUMMARY.SUMMARY_CLAIMED]: "",
  },
  // original details of previously certified (from history)
  previousCertified: null,
  matchClaimed: null,
  previousOption: null,

  paidValue: 0,
  lessRetention: 0,
  previousType: "cert_to_date",
  claimDetail: {},

  emailToList: [],
  emailSubject: "",
  isSendEmail: true,
  sendingEmail: false,
  approvingCert: false,
  autoNotify: [],

  approvalList: [],
  leaveComment: false,
  approval_comment: "",
  isAuditAttached: false,

  cert_attachments: [],
  shared_cert_attachments: [],
  attachedEmailFiles: [],
  exceedAttachLimit: false,

  reasonOption: [],

  projectInfo: [],
  certId: "",
  contractConfig: {},

  uploadFiles: [],
  editFileList: [],
  contractLinked: false,
  certifyPendingVariation: false,
  cis_status: "",
  certBulkSending: false,
  // page add/edit lineitem component states
  page_lineitem_add_edit: {
    expands: [], // expanded line items
    expandKeys: [], // item being edited or adding a child to
    expandChildKeys: [], // children of item being edited or adding stuff to
    parentId: "",
    lastChildKeys: [],
    expandEdit: false,
    expandAdd: false,
    addingItem: false,
    isVariationTypeDialogOpen: false,
    isVariationFormDialogOpen: false,
  },
  invalid_lineitem: {},
  invalid_lineitem_error: false, // use to colour lineitems and add component with a red border
  external_notes: "",
  internal_notes: "",
  certStatus: "",
});

// Helper function to determine updated values based on Variation Status
const getUpdatedValuesForVariations = (
  item,
  current_status,
  certifyPending,
) => {
  let defaultValues = {
    cert_value: 0,
    cert_percent: 0,
    cert_qty: 0,
  };

  if (!item) {
    return defaultValues;
  }

  // if certify pending variation toggle is on
  if (certifyPending) {
    switch (current_status) {
      case VARIATION_LINEITEM_STATUS.APPROVED:
      case VARIATION_LINEITEM_STATUS.REJECTED:
        defaultValues.cert_value = item?.claim_value ?? 0;
        defaultValues.cert_percent = item?.claim_percent ?? 0;
        defaultValues.cert_qty = item?.claim_qty ?? 0;
        break;
      case VARIATION_LINEITEM_STATUS.PENDING:
        defaultValues.cert_value = item?.cert_to_date_value ?? 0;
        defaultValues.cert_percent = item?.cert_to_date_percent ?? 0;
        defaultValues.cert_qty = item?.cert_to_date_qty ?? 0;
        break;
      default:
        break;
    }
    return defaultValues;
  }

  switch (current_status) {
    // If the status is approved, set the values to the claim values
    case VARIATION_LINEITEM_STATUS.APPROVED:
      return {
        cert_value: item.claim_value || 0,
        cert_percent: item.claim_percent || 0,
        cert_qty: item.claim_qty || 0,
      };

    // Set the values to 0 if the status is rejected or Pending
    case VARIATION_LINEITEM_STATUS.PENDING:
    case VARIATION_LINEITEM_STATUS.REJECTED:
      return defaultValues;
    default:
    // do nothing
  }
};

const addChildToCertList = (item, claimList) => {
  const isParent = checkIsParent(item);
  const item_qty = parseFloatValue(item?.quantity);
  const data = {
    id: item.id,
    payment_percent: item.cert_percent || 0,
    payment_value: item.cert_value || 0,
    payment_comment: item.cert_reason || "",
    parent_id: item.parent_id,
    total: item.total,
    claim_total: item.claim_value,
    has_child: isParent,
    required_reason:
      item.claim_value !== item.cert_value &&
      item.cert_reason === "" &&
      !isParent,
    claim_qty: item?.claim_qty || 0,
    item_description: item.description,
    item_rate: parseFloatValue(item.rate),
    total_item_quantity: item_qty,
    total_item_unit: parseFloatValue(item.unit),
    action_field: item?.cert_option || "percent",
    cert_option: item?.cert_option || "percent",
    exclude_retention: item?.exclude_retention,
    cert_qty: item?.cert_qty === null ? item?.claim_qty : item?.cert_qty || 0,
    current_status: item?.current_status || "",
    last_period: item?.last_period || 0,
    this_cert: item?.this_cert || 0,
    this_claim: item?.current_claim || 0,
  };

  if (isParent) {
    (item.childitems || []).forEach((child) => {
      claimList = addChildToCertList(child, claimList);
    });

    const children = claimList.filter((child) => child.parent_id === item.id);

    // get reason status of parent based on children
    data.reason_status =
      getReasonStatus(data, children) ||
      CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
  } else {
    // get reason status of children
    data.reason_status =
      getReasonStatus(data) || CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
  }

  claimList.push(data);

  return claimList;
};

const calculateCertTotal = (data) => {
  let certList = [],
    certTotal = 0,
    certTotalExcludeRetention = 0,
    thisCertTotal = 0,
    claim_to_date_total = 0,
    cert_to_date_total = 0,
    last_cert_total = 0,
    current_claim_total = 0,
    last_period_total = 0,
    this_claim_total = 0,
    this_cert_total = 0;
  if (data) {
    data.forEach((item) => {
      certTotal = addValues([certTotal, item.cert_value]);
      thisCertTotal = addValues([
        thisCertTotal,
        subtractValues([item.cert_value, item.cert_to_date_value]),
      ]);
      claim_to_date_total = addValues([claim_to_date_total, item.claim_value]);
      cert_to_date_total = addValues([
        cert_to_date_total,
        item.cert_to_date_value,
      ]);
      last_cert_total = addValues([last_cert_total, item.last_cert_value]);
      current_claim_total = addValues([
        current_claim_total,
        item.current_claim,
      ]);
      last_period_total = addValues([last_period_total, item.last_period]);
      this_cert_total = addValues([this_cert_total, item.this_cert]);
      this_claim_total = addValues([this_claim_total, item.current_claim]);
      certList = addChildToCertList(item, certList);
    });
  }

  for (const obj of certList) {
    if (!obj?.exclude_retention && !obj?.has_child) {
      certTotalExcludeRetention = addValues([
        certTotalExcludeRetention,
        obj.payment_value,
      ]);
    }
  }

  return [
    certList,
    certTotal,
    certTotalExcludeRetention,
    thisCertTotal,
    {
      claim_to_date_total,
      cert_to_date_total,
      last_cert_total,
      current_claim_total,
      last_period_total,
      this_claim_total,
      this_cert_total,
    },
  ];
};

// setCertInfo
const setInitialCertInfo = (state, action) => {
  //cert id and claim id exist
  const data = action.certInfo;
  let [
    baseCertList,
    baseCertTotal,
    baseCertTotalExcludeRetention,
    baseThisCertTotal,
    baseTotalValues,
  ] = calculateCertTotal(data.baseitems);
  let [
    variationCertList,
    variationCertTotal,
    variationCertTotalExcludeRetention,
    variationThisCertTotal,
    variationsTotalValues,
  ] = calculateCertTotal(data.variation_items);

  // update variation items with new child line Item if Multi-Select is used
  // Sort The Initial variations line items by created_date ascending
  data.variation_items = sortTable(
    getUpdatedVariationLineItems(data.variation_items),
    "created_date",
    "asc",
  );

  let claim_detail = {
    claim_ref: data.payclaim_name,
    claim_from: formatInTimeZone(
      data?.claim_from,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_to: formatInTimeZone(
      data?.claim_to,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_due_date: formatInTimeZone(
      data?.claim_last,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    cert_due_date: formatInTimeZone(
      data?.claim_cert,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    pay_due_date: formatInTimeZone(
      data?.claim_due,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_internal_notes: data.claim_internal_notes,
    claim_external_notes: data.claim_external_notes,
    previous_option: data.previous_option,
    has_retention: data.has_retention,
    read_retention: {
      pcd_date: formatDate(data.pcd_date),
      dlp_date: formatDate(data.dlp_date),
    },
    claimer_name: data.payee_entity_name,
  };
  let contacts = data.email_info;
  let attachedEmailFiles = data.email_info.attachments || [];
  attachedEmailFiles.map((file) => {
    if (!file.attach_method) {
      file.attach_method = data.attach_method;
    }
    return file;
  });

  const [materialsCertList, materialsTotal, materialsTotalExcludeRetention] =
    setMaterialCertListWithTotal(data.materials, true);

  // filter out invalid to_contacts (null or empty string emails)
  let toList =
    contacts?.to_contacts?.filter((item) => item?.contact_email) ?? [];

  let summary = {
    previous: data?.previous || {},
    claimedToDate: data?.claim_to_date_summary || {},
    thisCert: data?.this || {},
    certToDate: data?.cert_to_date_summary || {},
  };

  return state.merge(
    fromJS({
      //project detailed info
      projectlist: fromJS(data),
      certificate_number: data.cert_certificate_ref,

      baseTotalValues: baseTotalValues,
      baseLastPeriodTotal: divideValues([
        baseTotalValues.last_period_total,
        100,
      ]),
      variationsTotalValues: variationsTotalValues,
      variationLastPeriodTotal: divideValues([
        variationsTotalValues.last_period_total,
        100,
      ]),

      baseContractTable: data.baseitems || [],
      baseCertList: baseCertList,
      baseCertTotal: divideValues([baseCertTotal, 100]),
      baseCertTotalExcludeRetention: divideValues([
        baseCertTotalExcludeRetention,
        100,
      ]),
      baseThisCertTotal: divideValues([baseThisCertTotal, 100]),

      variationsTable: data.variation_items || [],
      variationCertList: variationCertList,
      variationCertTotal: divideValues([variationCertTotal, 100]),
      variationCertTotalExcludeRetention: divideValues([
        variationCertTotalExcludeRetention,
        100,
      ]),
      variationThisCertTotal: divideValues([variationThisCertTotal, 100]),

      materialsTable: data.materials || [],
      materialsCertList: materialsCertList || [],
      materialsTotal: materialsTotal,
      materialsTotalExcludeRetention: materialsTotalExcludeRetention,

      paidValue: divideValues([data.less_previous_paid_to_date, 100]) || "",
      lessRetention: divideValues([data.less_retention, 100]) || "",
      dlpReleaseValue: divideValues([data.dlp_release_value, 100]),
      pcdReleaseValue: divideValues([data.pcd_release_value, 100]),
      claimDetail: claim_detail,
      loading: false,
      emailToList: toList,
      previousType: data.previous_paid_to_date,
      isSendEmail: data.has_email_service || toList.length > 0,
      cert_attachments: data.attachments || [],
      shared_cert_attachments: fromJS(data.shared_attachments || []),
      isAuditAttached: data.attach_audit_trail,
      attachedEmailFiles: fromJS(attachedEmailFiles),
      retentionOption: data.retention_option,
      cis_status: data?.cis_status,
      domestic_reverse_charge: data?.domestic_reverse_charge,
      summaryComponent: summary,
      internal_notes: data.internal_notes,
      external_notes: data.external_notes,
      previousOption: data.previous?.option,
      certStatus: data.cert_status,
    }),
  );
};

const addChildToClaimReviewList = (item, claimList) => {
  let isParent = checkIsParent(item);

  let item_qty = parseFloatValue(item?.quantity);
  let list = {
    id: item.id,
    payment_percent: item.claim_percent,
    payment_value: item.claim_value,
    total: item.total,
    parent_id: item.parent_id,
    payment_comment: "",
    required_reason: false,
    has_child: isParent,
    claim_total: item.claim_value,
    claim_qty: item?.claim_qty || 0,
    item_description: item.description,
    exclude_retention: item?.exclude_retention,
    item_rate: parseFloatValue(item.rate),
    total_item_quantity: item_qty,
    total_item_unit: parseFloatValue(item.unit),
    action_field: item?.cert_option || "percent",
    cert_option: item?.cert_option || "percent",
    cert_qty: item?.cert_qty === null ? item?.claim_qty : item?.cert_qty || 0,
    last_period: item?.last_period || 0,
    this_cert: item?.this_cert || 0,
  };
  claimList.push(list);
  if (!isParent) {
    return claimList;
  } else {
    item.childitems.map((child) => {
      return (claimList = addChildToClaimReviewList(child, claimList));
    });
    return claimList;
  }
};

const calculateTotal = (data) => {
  let certList = [],
    claim_to_date_total = 0,
    cert_to_date_total = 0,
    last_cert_total = 0,
    current_claim_total = 0;
  if (data) {
    data.forEach((item) => {
      claim_to_date_total = addValues([claim_to_date_total, item.claim_value]);
      cert_to_date_total = addValues([
        cert_to_date_total,
        item.cert_to_date_value,
      ]);
      last_cert_total = addValues([last_cert_total, item.last_cert_value]);
      current_claim_total = addValues([
        current_claim_total,
        item.current_claim,
      ]);
      certList = addChildToClaimReviewList(item, certList);
    });
  }

  certList.forEach((item) => {
    item.has_children = certList.some(
      (otherItem) => otherItem?.parent_id === item?.id,
    );
  });

  return [
    certList,
    {
      claim_to_date_total,
      cert_to_date_total,
      last_cert_total,
      current_claim_total,
    },
  ];
};

const setClaimInfo = (state, action) => {
  //cert-id not exist & claim-id exist
  const data = action.projectinfo;
  let [baseCertList, baseTotalValues] = calculateTotal(data.baseitems);
  let [variationCertList, variationsTotalValues] = calculateTotal(
    data.variation_items,
  );

  const [materialsCertList, materialsTotal, materialsTotalExcludeRetention] =
    setMaterialCertListWithTotal(data.materials);

  let claim_detail = {
    claim_ref: data.payclaim_name,
    claim_from: formatInTimeZone(
      data?.claim_from,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_to: formatInTimeZone(
      data?.claim_to,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_due_date: formatInTimeZone(
      data?.claim_last,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    cert_due_date: formatInTimeZone(
      data?.claim_cert,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    pay_due_date: formatInTimeZone(
      data?.claim_due,
      "yyyy-MM-dd",
      data?.contract_timezone,
    ),
    claim_external_notes: data.claim_external_notes,
    has_retention: data.has_retention,
    read_retention: {
      pcd_date: formatDate(data.pcd_date),
      dlp_date: formatDate(data.dlp_date),
    },
    claimer_name: data.payee_entity_name,
  };

  return state.merge(
    fromJS({
      //project detailed info
      certificate_number: "",
      projectlist: data,

      baseContractTable: data.baseitems || [],
      baseCertTotal: divideValues([data.total_claim_base_contract, 100]),
      baseCertTotalExcludeRetention: divideValues([
        data.total_claim_base_contract_exclude_retention,
        100,
      ]),
      baseCertList: baseCertList,

      baseTotalValues: baseTotalValues,
      variationsTotalValues: variationsTotalValues,

      variationsTable: data.variation_items || [],
      variationCertList: variationCertList,
      variationCertTotal: divideValues([data.total_claim_variation, 100]),
      variationCertTotalExcludeRetention: divideValues([
        data.total_claim_variation_exclude_retention,
        100,
      ]),

      materialsTable: data.materials || [],
      materialsCertList: materialsCertList || [],
      materialsTotal: materialsTotal,
      materialsTotalExcludeRetention: materialsTotalExcludeRetention,

      paidValue: divideValues([data.claim_less_paid_to_date, 100]) || "",
      claimDetail: claim_detail,
      loading: false,
      previousType: data.previous_paid_to_date,
      shared_cert_attachments: fromJS(data.shared_attachments || []),
      dlpReleaseValue: divideValues([data.dlp_release_value, 100]) || 0,
      pcdReleaseValue: divideValues([data.pcd_release_value, 100]) || 0,
      labour_element: data.cert_to_date_summary?.labour_element,
      previous_labour_element: data.previous?.labour_element,
      previous_cis_deduction: data.previous?.cis_deduction,
      cis_deduction: data?.cert_to_date_summary?.cis_deduction,
      cis_status: data?.cis_status,
      certStatus: data?.cert_status,
    }),
  );
};

function findParent(itemList, table, parent_id) {
  if (!parent_id) {
    return itemList;
  }
  let parent_obj = findTreeNode(parent_id, table.toJS());
  let parent_total = 0;
  let last_period_total = 0;
  let this_cert_total = 0;
  let this_claim_total = 0;

  parent_obj?.childitems?.forEach((item) => {
    var obj = itemList.find((ele) => ele.id === item.id);
    if (obj) {
      parent_total = addValues([parent_total, obj.payment_value || 0]);
      last_period_total = addValues([last_period_total, obj.last_period || 0]);
      this_cert_total = addValues([this_cert_total, obj.this_cert || 0]);
      this_claim_total = addValues([this_claim_total, obj.current_claim || 0]);
    }
  });
  var parent_obj_cert = itemList.find((ele) => ele.id === parent_obj.id);
  var children_obj_certs = itemList.filter(
    (ele) => ele.parent_id === parent_obj.id,
  );

  parent_obj_cert.payment_percent =
    parent_obj_cert.total !== 0
      ? multiplyValues([
          divideValues([parent_total, parent_obj_cert.total]),
          100,
        ])
      : 0;
  parent_obj_cert.payment_value = parent_total;
  //since parent qty always be 1. so cert_qty = payment_percent
  parent_obj_cert.cert_qty = divideValues([
    parent_obj_cert.payment_percent,
    100,
  ]);
  parent_obj_cert.required_reason = checkIfRequired(parent_obj_cert);
  parent_obj_cert.reason_status = getReasonStatus(
    parent_obj_cert,
    children_obj_certs,
  );
  parent_obj_cert.last_period = last_period_total;
  parent_obj_cert.this_claim = this_claim_total;
  parent_obj_cert.this_cert = this_cert_total;
  itemList = findParent(itemList, table, parent_obj_cert.parent_id);
  return itemList;
}

function checkIfRequired(item) {
  return (
    item?.payment_comment === "" &&
    (item?.claim_total || 0) !== (item?.payment_value || 0) &&
    !item.has_child
  );
}

const getReasonStatus = (item, childItems = []) => {
  const { payment_comment, current_status, claim_total, payment_value } = item;

  if (payment_comment === "") {
    switch (current_status) {
      // if pending and has a claim, only show error on root level
      case VARIATION_LINEITEM_STATUS.PENDING:
        // check if childItems require a variance reason
        if (childItems.length !== 0 && item.parent_id === "") {
          const needsReason = childItems.some(
            (item) =>
              item.required_reason === true &&
              item.reason_status === CERT_VARIATION_ACTION_TYPES.REASON_EMPTY,
          );

          return needsReason
            ? CERT_VARIATION_ACTION_TYPES.REASON_ERROR
            : CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
        }

        return claim_total > 0 &&
          !item.parent_id &&
          claim_total !== payment_value
          ? CERT_VARIATION_ACTION_TYPES.REASON_ERROR
          : CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
      // if approved
      case VARIATION_LINEITEM_STATUS.APPROVED:
        // if there’s variance for child line item, reason for variance is only mandatory for that child line item and not mandatory for the parent.
        // so only to show error on base vari and child line items instead of parent line item or root level
        return claim_total !== payment_value && !item.has_child
          ? CERT_VARIATION_ACTION_TYPES.REASON_ERROR
          : CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
      // if rejected
      case VARIATION_LINEITEM_STATUS.REJECTED:
        return claim_total > 0
          ? CERT_VARIATION_ACTION_TYPES.REASON
          : CERT_VARIATION_ACTION_TYPES.REASON_EMPTY;
    }
  }
  return CERT_VARIATION_ACTION_TYPES.REASON;
};

// setClaimList
const setCertList = (state, action) => {
  //action.name : base/variation
  let cert_list_name = action.name + "CertList",
    cert_table_name =
      action.name === "base"
        ? action.name + "ContractTable"
        : action.name === "variation"
          ? action.name + "sTable"
          : action.name + "Table";
  let certList = state.get(cert_list_name).toJS(),
    table = state.get(cert_table_name),
    certTotal = 0,
    certLastPeriodTotal = 0,
    certThisTotal = 0,
    certThisClaimTotal = 0,
    certTotalExcludeRetention = 0,
    parent_id = "";

  // set reason_status here
  if (action.field === "value") {
    certList = certList.map((item) => {
      if (item.id === action.id) {
        item.payment_value =
          roundNearestInt(multiplyValues([action.value, 100])) || 0;
        item.payment_percent =
          item.total !== 0
            ? multiplyValues([
                divideValues([item.payment_value, item.total]),
                100,
              ])
            : 0;
        item.cert_qty = itemQuantity(
          item.payment_value,
          item.total,
          item.total_item_quantity,
        );
        parent_id = item.parent_id;
        item.required_reason = checkIfRequired(item);
        item.reason_status = getReasonStatus(item);
      }
      return item;
    });
  } else if (action.field === "qty") {
    //handle qty
    certList = certList.map((item) => {
      if (item.id === action.id) {
        //if user type qty from input box
        if (!action.fromPrctQty && !action.fromLoad) {
          item.cert_qty = action.value;
          item.payment_value =
            multiplyValues([item.cert_qty, item?.item_rate]) || 0;
        }

        //if by clicking switch button, no need to care action value
        if (action.fromPrctQty && !action.fromLoad) {
          item.cert_qty = divideValues([
            multiplyValues([item.payment_percent, item.total_item_quantity]),
            100,
          ]);
        }

        item.payment_percent =
          item.total !== 0
            ? multiplyValues([
                divideValues([item.payment_value, item.total]),
                100,
              ])
            : 0;
        parent_id = item.parent_id;
        item.required_reason = checkIfRequired(item);
        item.reason_status = getReasonStatus(item);
      }
      return item;
    });
  } else if (action.field === "last_period") {
    certList = certList.map((item) => {
      if (item.id === action.id) {
        item.last_period = multiplyValues([action.value, 100]) || 0;
        parent_id = item.parent_id;
      }
      return item;
    });
  } else if (action.field === "this_cert") {
    const this_cert_value = multiplyValues([action.value, 100]) || 0;
    certList = certList.map((item) => {
      if (item.id === action.id) {
        let payment_value = addValues([
          this_cert_value,
          item?.last_period || 0,
        ]);
        item.payment_value = payment_value;
        parent_id = item.parent_id;
      }
      return item;
    });
  } else {
    //handle percent
    certList = certList.map((item) => {
      if (item.id === action.id) {
        //if user typed value
        if (!action.fromLoad && !action.fromPrctQty) {
          item.payment_percent = parseFloatValue(action.value) || 0;
          item.payment_value = roundNearestInt(
            divideValues([
              multiplyValues([item.payment_percent, item.total]),
              100,
            ]),
          );
        }

        //if by clicking switch button, no need to care action value
        if (!action.fromLoad && action.fromPrctQty) {
          item.payment_percent =
            item.total !== 0
              ? multiplyValues([
                  divideValues([item.payment_value, item.total]),
                  100,
                ])
              : 0;
        }
        item.cert_qty = divideValues([
          multiplyValues([item.payment_percent, item.total_item_quantity]),
          100,
        ]);
        parent_id = item.parent_id;
        item.required_reason = checkIfRequired(item);
        item.reason_status = getReasonStatus(item);
      }
      return item;
    });
  }

  certList.forEach((item) => {
    item.has_children = certList.some(
      (otherItem) => otherItem?.parent_id === item?.id,
    );
  });

  if (parent_id !== "") {
    certList = findParent(certList, table, parent_id);
  }

  table.forEach((item) => {
    var obj = certList.find((ele) => ele.id === item.get("id"));
    if (obj) {
      certTotal = addValues([certTotal, obj.payment_value]);
      certLastPeriodTotal = addValues([
        certLastPeriodTotal,
        obj.last_period || 0,
      ]);
      certThisTotal = addValues([certThisTotal, obj.this_cert || 0]);
      certThisClaimTotal = addValues([
        certThisClaimTotal,
        obj.current_claim || 0,
      ]);
    }
  });

  for (const obj of certList) {
    if (!obj?.exclude_retention && !obj?.has_children) {
      certTotalExcludeRetention = addValues([
        certTotalExcludeRetention,
        obj.payment_value,
      ]);
    }
  }

  let newState = state.merge(
    fromJS({
      [cert_list_name]: certList,
      [action.name + "CertTotal"]: divideValues([certTotal, 100]),
      [action.name + "CertTotalExcludeRetention"]: divideValues([
        certTotalExcludeRetention,
        100,
      ]),
      [action.name + "LastPeriodTotal"]: divideValues([
        certLastPeriodTotal,
        100,
      ]),
      [action.name + "ThisCertTotal"]: divideValues([certThisTotal, 100]),
      [action.name + "ThisClaimTotal"]: divideValues([certThisClaimTotal, 100]),
    }),
  );

  if (action.name === "base") {
    let [
      baseCertList, // eslint-disable-line
      baseCertTotal, // eslint-disable-line
      baseCertTotalExcludeRetention, // eslint-disable-line
      baseThisCertTotal, // eslint-disable-line
      baseTotalValues,
    ] = calculateCertTotal(certList);

    newState = newState
      .setIn(
        ["baseTotalValues", "last_period_total"],
        baseTotalValues.last_period_total,
      )
      .setIn(
        ["baseTotalValues", "this_cert_total"],
        certTotal - certLastPeriodTotal,
      );
  }

  return newState;
};

const set_reason = (state, action) => {
  let name = action.field + "CertList",
    list = state.get(name).toJS();
  let objIndex = list.findIndex((item) => item.id === action.id);
  if (objIndex === -1) return state; // safety
  let obj = list[objIndex];

  // Update the current item
  obj.payment_comment = action.reason;
  if (
    action.reason === "" &&
    obj.claim_total !== obj.payment_value &&
    obj.current_status === VARIATION_LINEITEM_STATUS.PENDING &&
    !obj.has_child
  ) {
    obj.required_reason = true;
  } else {
    obj.required_reason = false;
  }
  obj.reason_status = getReasonStatus(obj);
  obj.reject_reason = action.reason;

  // If has children, update them
  if (obj.has_child) {
    list.forEach((childItem) => {
      if (childItem.parent_id === obj.id) {
        const isRequiredReason =
          action.reason === "" &&
          childItem?.claim_total !== childItem?.payment_value &&
          childItem?.current_status === VARIATION_LINEITEM_STATUS.PENDING &&
          !childItem.has_child;

        childItem.required_reason = isRequiredReason;
        childItem.reason_status = getReasonStatus(childItem);
      }
    });
  }

  const updateParentChain = (parentId) => {
    const parent = list.find((item) => item.id === parentId);
    if (!parent) return;

    const anyChildRequires = list.some(
      (it) => it.parent_id === parent.id && it.required_reason === true,
    );

    parent.required_reason = anyChildRequires;

    if (!anyChildRequires) {
      // Parent is only flagged because a child requires it
      parent.reason_status = CERT_VARIATION_ACTION_TYPES.REASON;
    } else {
      // Normal status calculation
      parent.reason_status = getReasonStatus(parent);
    }

    if (parent.parent_id) {
      updateParentChain(parent.parent_id); // climb further up
    }
  };

  // If the current object is a child or subchild, walk up the chain
  if (obj.parent_id) {
    updateParentChain(obj.parent_id);
  }

  return state.merge({
    [name]: fromJS(list),
  });
};

const setClaimEmailList = (state, action) => {
  let list = {},
    name = "",
    field = action.field.replace(/^\S/, (s) => s.toUpperCase());
  name = "email" + field + "List";
  list = state.get(name).toJS();
  list = list.filter(
    (chip) => chip.contact_email !== action.data.get("contact_email"),
  );
  return state.set(name, fromJS(list));
};

const addClaimEmailList = (state, action) => {
  let list = {},
    name = "",
    field = action.field.replace(/^\S/, (s) => s.toUpperCase());
  name = "email" + field + "List";
  let newContact = {};
  if (action.data.contact_name === "") {
    newContact.contact_name = action.data.contact_email;
  } else newContact.contact_name = action.data.contact_name;
  newContact.contact_email = action.data.contact_email;

  list = state.get(name).toJS();
  list = list.filter(
    (chip) => chip.contact_email !== action.data.contact_email,
  );
  list = list.concat(newContact);
  return state.set(name, fromJS(list));
};

const setLineitemComments = (state, action) => {
  const { lineId, variationItems } = action;
  let stateUpdate = { ...state.toJS() };

  // Update only the comments for the specific lineId in variationsTable
  let itemUpdate = getNestedLineitem(variationItems, lineId);
  let found = false;
  stateUpdate.variationsTable = state
    .get("variationsTable")
    .toJS()
    .map((item) => {
      if (!found && (item.id === lineId || item.childitems?.length > 0)) {
        item = updateDescendantLineitem(item, lineId, (obj) => {
          return { ...obj, comments: itemUpdate.comments };
        });
      }

      // Return the original item if it doesn't match the lineId
      return item;
    });

  stateUpdate.baseContractTable =
    action.baseItems || state.get("baseContractTable").toJS();

  stateUpdate.materialsTable =
    action.materialItems || state.get("materialsTable").toJS();

  return state.merge(fromJS(stateUpdate));
};

const setMaterialInput = (state, action) => {
  let materialsCertList = state.get("materialsCertList").toJS();
  let materialsTotal = state.get("materialsTotal").toJS();
  let materialsTotalExcludeRetention = state
    .get("materialsTotalExcludeRetention")
    .toJS();
  let onSiteCertTotal = 0,
    offSiteCertTotal = 0;
  let onSiteCertTotalExcludeRetention = 0,
    offSiteCertTotalExcludeRetention = 0;
  let onSiteCertLastPeriodTotal = 0,
    offSiteCertLastPeriodTotal = 0;

  materialsCertList = materialsCertList.map((lineItem) => {
    if (lineItem.id === action.lineId) {
      if (action.field === "last_period") {
        lineItem.last_period = multiplyValues([action.value, 100]) || 0;
      }
      if (action.field === "payment_value") {
        lineItem.payment_value = multiplyValues([action.value, 100]) || 0;
      }
      if (action.field === "this_cert") {
        if (!lineItem.last_period) {
          lineItem.last_period = 0;
        }
        lineItem.payment_value =
          multiplyValues([
            addValues([
              divideValues([lineItem.last_period, 100]),
              action.value,
            ]),
            100,
          ]) || 0;
      }
      lineItem.required_reason =
        lineItem.claim_total !== multiplyValues([action.value, 100]) &&
        lineItem.payment_comment === "";
    }
    if (lineItem.onSite) {
      onSiteCertTotal = addValues([onSiteCertTotal, lineItem.payment_value]);
      onSiteCertLastPeriodTotal = addValues([
        onSiteCertLastPeriodTotal,
        lineItem.last_period || 0,
      ]);

      if (!lineItem.exclude_retention) {
        onSiteCertTotalExcludeRetention = addValues([
          onSiteCertTotalExcludeRetention,
          lineItem.payment_value,
        ]);
      }
    } else {
      offSiteCertTotal = addValues([offSiteCertTotal, lineItem.payment_value]);
      offSiteCertLastPeriodTotal = addValues([
        offSiteCertLastPeriodTotal,
        lineItem.last_period || 0,
      ]);

      if (!lineItem.exclude_retention) {
        offSiteCertTotalExcludeRetention = addValues([
          offSiteCertTotalExcludeRetention,
          lineItem.payment_value,
        ]);
      }
    }
    return lineItem;
  });

  return state.merge({
    materialsCertList: fromJS(materialsCertList),
    materialsTotal: fromJS({
      ...materialsTotal,
      onSite: onSiteCertTotal,
      offSite: offSiteCertTotal,
      onSiteLastPeriod: onSiteCertLastPeriodTotal,
      offSiteLastPeriod: offSiteCertLastPeriodTotal,
    }),
    materialsTotalExcludeRetention: fromJS({
      ...materialsTotalExcludeRetention,
      onSite: onSiteCertTotalExcludeRetention,
      offSite: offSiteCertTotalExcludeRetention,
    }),
  });
};

const setMaterialCertListWithTotal = (data, isCert = false) => {
  const materialsCertList = data
    ? data.map((item) => ({
        id: item.id,
        payment_value: isCert ? item.cert_value : item.claim_value,
        current_claim: item.current_claim,
        claim_value: item.claim_value,
        onSite: item.on_site,
        payment_comment: item.cert_reason || "",
        last_period: item.last_period || 0,
        claim_total: item.claim_value,
        exclude_retention: item?.exclude_retention,
        required_reason:
          item.claim_value !== item.cert_value && item.cert_reason === "",
      }))
    : [];

  const materialsTotal = {
    onSiteClaim: 0,
    onSite: 0,
    offSiteClaim: 0,
    offSite: 0,
    onSiteClaimToDate: 0,
    offSiteClaimToDate: 0,
    onSiteLastPeriod: 0,
    offSiteLastPeriod: 0,
  };

  const materialsTotalExcludeRetention = {
    onSiteClaim: 0,
    onSite: 0,
    offSiteClaim: 0,
    offSite: 0,
  };

  materialsCertList?.forEach((value) => {
    if (value.onSite) {
      materialsTotal.onSiteClaim = addValues([
        materialsTotal.onSiteClaim,
        value.current_claim,
      ]);
      materialsTotal.onSite = addValues([
        materialsTotal.onSite,
        value.payment_value,
      ]);
      materialsTotal.onSiteClaimToDate = addValues([
        materialsTotal.onSiteClaimToDate,
        value.claim_value,
      ]);
      materialsTotal.onSiteLastPeriod = addValues([
        materialsTotal.onSiteLastPeriod,
        value.last_period || 0,
      ]);

      if (!value.exclude_retention) {
        materialsTotalExcludeRetention.onSiteClaim = addValues([
          materialsTotalExcludeRetention.onSiteClaim,
          value.current_claim,
        ]);
        materialsTotalExcludeRetention.onSite = addValues([
          materialsTotalExcludeRetention.onSite,
          value.payment_value,
        ]);
      }
    } else {
      materialsTotal.offSiteClaim = addValues([
        value.current_claim,
        materialsTotal.offSiteClaim,
      ]);
      materialsTotal.offSite = addValues([
        materialsTotal.offSite,
        value.payment_value,
      ]);
      materialsTotal.offSiteClaimToDate = addValues([
        materialsTotal.offSiteClaimToDate,
        value.claim_value,
      ]);
      materialsTotal.offSiteLastPeriod = addValues([
        materialsTotal.offSiteLastPeriod,
        value.last_period || 0,
      ]);

      if (!value.exclude_retention) {
        materialsTotalExcludeRetention.offSiteClaim = addValues([
          value.current_claim,
          materialsTotalExcludeRetention.offSiteClaim,
        ]);
        materialsTotalExcludeRetention.offSite = addValues([
          value.payment_value,
          materialsTotalExcludeRetention.offSite,
        ]);
      }
    }
  });

  return [materialsCertList, materialsTotal, materialsTotalExcludeRetention];
};

const removeFileFromList = (state, action) => {
  const fileList = state.get("uploadFiles");

  let fList1 = fileList.filter((o) => o?.name !== action.fileName);
  let fList2 = fList1.filter((o) => o?.file_name !== action.fileName);

  return state.set("uploadFiles", fList2);
};

export const certSummaryCalculatedValues = (
  certToDateData,
  previousData,
  thisData,
  gst,
  cisStatus,
  domestic_reverse_charge,
) => {
  const calculateAndAssignTotals = (data) => {
    const [total, gstTotal] = calculateTotalAndGst(data, gst);
    data.total = total;
    data.gst = gstTotal;
  };

  calculateAndAssignTotals(certToDateData);
  calculateAndAssignTotals(previousData);
  const calculateCISValues = (data, cisStatus) => {
    const cis_gross = data?.labour_element ?? 0;
    const gross = data?.gross ?? 0;
    if (cis_gross === 0) {
      return { gross: cis_gross, retention: 0, pcrr: 0, dlrr: 0, value: 0 };
    }
    const safeValue = (value) => (isNaN(value) || !isFinite(value) ? 0 : value);
    const retention = safeValue(
      multiplyValues([divideValues([cis_gross, gross]), data.retention]),
    );
    const retention_pcrr = safeValue(
      multiplyValues([divideValues([cis_gross, gross]), data.retention_pcrr]),
    );
    const retention_dlrr = safeValue(
      multiplyValues([divideValues([cis_gross, gross]), data.retention_dlrr]),
    );
    const value = safeValue(
      divideValues([
        multiplyValues([
          addValues([cis_gross, -retention, retention_dlrr, retention_pcrr]),
          cisStatus,
        ]),
        100,
      ]),
    );

    return {
      gross: cis_gross,
      retention,
      pcrr: retention_pcrr,
      dlrr: retention_dlrr,
      value,
    };
  };

  certToDateData.cis_deduction = calculateCISValues(certToDateData, cisStatus);
  previousData.cis_deduction = calculateCISValues(previousData, cisStatus);

  thisData = {
    gross: subtractValues([certToDateData.gross ?? 0, previousData.gross ?? 0]),
    retention: subtractValues([
      certToDateData.retention ?? 0,
      previousData.retention ?? 0,
    ]),
    retention_pcrr: subtractValues([
      certToDateData.retention_pcrr ?? 0,
      previousData.retention_pcrr ?? 0,
    ]),
    retention_dlrr: subtractValues([
      certToDateData.retention_dlrr ?? 0,
      previousData.retention_dlrr ?? 0,
    ]),
    labour_element: subtractValues([
      certToDateData.labour_element ?? 0,
      previousData.labour_element ?? 0,
    ]),
    cis_deduction: {
      value: subtractValues([
        certToDateData?.cis_deduction?.value ?? 0,
        previousData?.cis_deduction?.value ?? 0,
      ]),
      gross: subtractValues([
        certToDateData?.cis_deduction?.gross ?? 0,
        previousData?.cis_deduction?.gross ?? 0,
      ]),
      dlrr: subtractValues([
        certToDateData?.cis_deduction?.dlrr ?? 0,
        previousData?.cis_deduction?.dlrr ?? 0,
      ]),
      pcrr: subtractValues([
        certToDateData?.cis_deduction?.pcrr ?? 0,
        previousData?.cis_deduction?.pcrr ?? 0,
      ]),
      retention: subtractValues([
        certToDateData?.cis_deduction?.retention ?? 0,
        previousData?.cis_deduction?.retention ?? 0,
      ]),
    },
  };

  calculateAndAssignTotals(thisData);
  thisData.gst = domestic_reverse_charge ? 0 : thisData.gst;
  thisData.value = subtractValues([
    addValues([thisData.total, thisData.gst]),
    thisData?.cis_deduction?.value ?? 0,
  ]);
  return [previousData, thisData, certToDateData];
};

const updateCertSummary = (state, action) => {
  let update = action.data;
  let cisStatus = action.cisStatus;
  let newState = { ...state.toJS() };
  let previousUpdate = {};
  let thisUpdate = {};
  let certToDateUpdate = {};
  const gst = newState.projectlist.gst;
  const oldSummary = newState.summaryComponent;
  const certToDate = newState.summaryComponent.certToDate;
  const domestic_reverse_charge = newState?.domestic_reverse_charge;
  previousUpdate.option =
    update?.previous_option || oldSummary.previous?.option;
  switch (update?.previous_option) {
    case "certified": {
      const previousCertified = newState.previousCertified;
      previousUpdate = {
        ...previousUpdate,
        object_id: previousCertified?.object_id || "",
        gross: previousCertified?.gross || 0,
        retention: previousCertified?.retention || 0,
        retention_pcrr: previousCertified?.retention_pcrr || 0,
        retention_dlrr: previousCertified?.retention_dlrr || 0,
        labour_element: previousCertified?.labour_element || 0,
      };
      break;
    }
    case "claimed": {
      const matchClaimed = newState.matchClaimed;
      previousUpdate = {
        ...previousUpdate,
        object_id: matchClaimed?.id || "",
        gross: matchClaimed?.gross || 0,
        retention: matchClaimed?.retention || 0,
        retention_pcrr: matchClaimed?.retention_pcrr || 0,
        retention_dlrr: matchClaimed?.retention_dlrr || 0,
        labour_element: matchClaimed?.labour_element || 0,
      };
      break;
    }
    default:
      previousUpdate = {
        ...previousUpdate,
        gross:
          typeof update["previous_gross"] === "number"
            ? update["previous_gross"]
            : oldSummary.previous?.gross,
        retention:
          typeof update["previous_retention"] === "number"
            ? update["previous_retention"]
            : oldSummary.previous?.retention,
        retention_pcrr:
          typeof update["previous_retention_pcrr"] === "number"
            ? update["previous_retention_pcrr"]
            : oldSummary.previous?.retention_pcrr,
        retention_dlrr:
          typeof update["previous_retention_dlrr"] === "number"
            ? update["previous_retention_dlrr"]
            : oldSummary.previous?.retention_dlrr,
        labour_element:
          typeof update["previous_labour_element"] === "number"
            ? update["previous_labour_element"]
            : oldSummary.previous?.labour_element,
      };
  }

  certToDateUpdate = {
    ...certToDate,
    retention_pcrr: update?.cert_retention_pcrr ?? certToDate.retention_pcrr,
    retention_dlrr: update?.cert_retention_dlrr ?? certToDate.retention_dlrr,
    labour_element: update?.labour_element ?? certToDate.labour_element,
  };

  const [previousUpdatedData, thisUpdatedData, certToDateUpdatedData] =
    certSummaryCalculatedValues(
      certToDateUpdate,
      previousUpdate,
      thisUpdate,
      gst,
      cisStatus,
      domestic_reverse_charge,
    );
  newState.summaryComponent.previous = previousUpdatedData;
  newState.summaryComponent.thisCert = thisUpdatedData;
  newState.summaryComponent.certToDate = certToDateUpdatedData;
  newState.previousOption = previousUpdate.option;

  return fromJS(newState);
};

const setPreviousCertified = (state, action) => {
  if (!Array.isArray(action?.data) || action.data.length === 0) {
    return state; // Data array is empty or undefined.
  }

  let stateUpdate = { ...state.toJS() };
  let prevCertified = null;
  const gst = stateUpdate.projectlist.gst;

  const currentCertIndex = action.data.findIndex(
    (item) => item.cert_id === action.currentCertId,
  );

  // Check the last approved cert.
  var i =
    action.data?.findIndex(
      (item, index) =>
        item.cert_status === STATUS_APPROVED && index > currentCertIndex,
    ) || -1;

  if (i >= 0) {
    const data = action.data[i];

    // set previous cert ref title
    stateUpdate.previousRef[CLAIM_CERT_SUMMARY.SUMMARY_CERTIFIED] =
      data?.cert_number;
    prevCertified = {
      object_id: data?.cert_id || "",
      gross: data?.gross_certified_value || 0,
      retention: data?.cert_retention || 0,
      retention_pcrr: data?.cert_pcd || 0,
      retention_dlrr: data?.cert_dlp || 0,
      // labour_element
      labour_element: data?.cert_labour_element || 0,
    };
    prevCertified["total"] = addValues([
      prevCertified.gross,
      prevCertified.retention_pcrr,
      prevCertified.retention_dlrr,
      -prevCertified.retention,
    ]);

    prevCertified["gst"] = multiplyValues([
      prevCertified["total"],
      divideValues([gst, 100]),
    ]);
  }
  stateUpdate.previousCertified = prevCertified;

  switch (action.data[currentCertIndex].claim_previous_option) {
    case CLAIM_CERT_SUMMARY.SUMMARY_CERTIFIED: {
      const certIndex = action.data.findIndex(
        (item) =>
          item.cert_id ===
          action.data[currentCertIndex].claim_previous_option_ref,
      );
      stateUpdate.previousRef[CLAIM_CERT_SUMMARY.SUMMARY_CLAIMED] =
        action.data[certIndex]?.cert_number;
      break;
    }
    case CLAIM_CERT_SUMMARY.SUMMARY_CLAIMED: {
      const claimedIndex = action.data.findIndex(
        (item) =>
          item.claim_id ===
          action.data[currentCertIndex].claim_previous_option_ref,
      );
      stateUpdate.previousRef[CLAIM_CERT_SUMMARY.SUMMARY_CLAIMED] =
        action.data[claimedIndex]?.claim_number;
      break;
    }
  }

  stateUpdate.matchClaimed = {
    matchClaimSelectedOption:
      action.data[currentCertIndex]?.claim_previous_option,
    object_id: action.data[currentCertIndex]?.id || "",
    gross: action.data[currentCertIndex]?.claim_previous_gross || 0,
    retention: action.data[currentCertIndex]?.claim_previous_retention || 0,
    retention_pcrr:
      action.data[currentCertIndex]?.claim_previous_retention_pcrr || 0,
    retention_dlrr:
      action.data[currentCertIndex]?.claim_previous_retention_dlrr || 0,
    labour_element:
      action.data[currentCertIndex]?.claim_previous_labour_element || 0,
  };

  return fromJS(stateUpdate);
};

const setVariationCertList = (
  variationCertList,
  updatedVariationsTable,
  updatedVariationItemFromCertList,
  currentVariationCertList,
) => {
  // Function to update child items
  const updateChildItems = (parentItem, certList) => {
    if (checkIsParent(parentItem)) {
      parentItem.childitems?.forEach((child) => {
        // Find and update the child item in certList
        const childCertItem = certList.find((claim) => claim.id === child.id);
        if (childCertItem) {
          // Replace the old child item with the updated one
          const updatedChildItem = variationCertList.find(
            (item) => item.id === child.id,
          );
          if (updatedChildItem) {
            Object.assign(childCertItem, updatedChildItem); // Update child item
          }
        }
        // Recursively update child items
        updateChildItems(child, certList);
      });
    }
  };

  // Update the variation cert list
  const updatedVariationClaimList = currentVariationCertList.map((certItem) => {
    if (
      updatedVariationItemFromCertList &&
      certItem.id === updatedVariationItemFromCertList.id
    ) {
      // Find the actual line item to determine if it's a parent
      const actualLineItem = updatedVariationsTable.find(
        (lineItem) => lineItem.id === updatedVariationItemFromCertList.id,
      );

      // Update the item and its child items if it's a parent
      const updatedItem = {
        ...certItem,
        ...updatedVariationItemFromCertList,
      };

      if (actualLineItem) {
        updateChildItems(actualLineItem, currentVariationCertList);
      }

      return updatedItem;
    }
    return certItem;
  });

  return updatedVariationClaimList;
};

const setVariationLineItemInfo = (state, action) => {
  const currentVariationsTable = state.get("variationsTable").toJS();
  const currentVariationCertList = state.get("variationCertList").toJS();
  const certifyPending = state.get("certifyPendingVariation");

  // Update item and child items
  const updateItem = (item) => {
    if (action?.lineItemDetails) {
      return action.lineItemDetails;
    }

    const currentStatus = action.current_status ?? item.current_status;

    const updatedValues = getUpdatedValuesForVariations(
      item,
      currentStatus,
      certifyPending,
    );

    const updatedChildItems = item.childitems
      ? item.childitems.map(updateItem) // Recursively update child items
      : [];

    return {
      ...item,
      current_status: currentStatus,
      ...updatedValues,
      childitems: updatedChildItems,
    };
  };

  // Update variations table
  const updatedVariationsTable = currentVariationsTable.map((lineItem) =>
    lineItem.id === action.lineitemId ? updateItem(lineItem) : lineItem,
  );

  const [
    variationCertList,
    variationCertTotal,
    variationCertTotalExcludeRetention,
    variationThisCertTotal,
    variationsTotalValues,
  ] = calculateCertTotal(updatedVariationsTable);
  const updatedVariationItemFromCertList = variationCertList.find(
    (lineItem) => lineItem.id === action.lineitemId,
  );

  const variationCertListUpdate = setVariationCertList(
    variationCertList,
    updatedVariationsTable,
    updatedVariationItemFromCertList,
    currentVariationCertList,
  );

  // Then, calculate the total variation after the status change
  let variationTotalsAfterStatus = 0;
  updatedVariationsTable.forEach((lineItem) => {
    if (
      [VARIATION_LINEITEM_STATUS.APPROVED].includes(lineItem.current_status)
    ) {
      variationTotalsAfterStatus = addValues([
        variationTotalsAfterStatus,
        lineItem.total,
      ]);
    }
  });
  // Update the total variation in the project data
  const currentProjectData = {
    ...state.get("projectlist").toJS(),
    total_variation: variationTotalsAfterStatus,
  };

  // Return the updated state with recalculated totals
  return state.merge(
    fromJS({
      variationsTable: updatedVariationsTable,
      projectlist: currentProjectData,
      variationCertTotal: calculateSubunitToUnit(variationCertTotal),
      variationCertList: variationCertListUpdate,
      variationCertTotalExcludeRetention: calculateSubunitToUnit(
        variationCertTotalExcludeRetention,
      ),
      variationThisCertTotal: calculateSubunitToUnit(variationThisCertTotal),
      variationsTotalValues,
    }),
  );
};

const handleTreeExpand = (state, action) => {
  let update = { ...state.get("page_lineitem_add_edit").toJS() };
  let key = action?.key || [];
  let expands = Array.isArray(key) ? [...key] : [key];

  if (action?.appendKey)
    expands = Array.from(new Set([...update?.expands, ...expands]));
  update.expands = expands;

  return state.merge({
    page_lineitem_add_edit: fromJS(update),
  });
};

export default (state = defaultState, action) => {
  switch (action.type) {
    case constants.REQUEST_CLAIM_INFO:
      //get initial claim info from api
      return setClaimInfo(state, action);
    case constants.REQUEST_CERT_INFO:
      return setInitialCertInfo(state, action);
    case constants.SET_INPUT_VALUE:
      return setCertList(state, action);
    case constants.SET_REASON_VALUE:
      return set_reason(state, action);
    case constants.SET_NAME_VALUE:
      return state.set(action.name, action.value);
    case constants.RESET_STAGE:
      return defaultState;
    case constants.REMOVE_EMAIL:
      return setClaimEmailList(state, action);
    case constants.ADD_EMAIL:
      return addClaimEmailList(state, action);
    case constants.SET_EMAIL_SUBJECT:
      return state.set(action.name, action.value);
    case constants.SET_ARRAY_DATA:
      return state.set(
        action.name,
        action.value ? fromJS(action.value) : fromJS([]),
      );
    case constants.SET_DATA:
      return state.set(action.payload.name, fromJS(action.payload.value));
    case constants.SET_LINE_COMMENTS:
      return setLineitemComments(state, action);
    case constants.SET_MATERIAL_INPUT:
      return setMaterialInput(state, action);
    case constants.DELETE_FILE:
      let files = state
        .get("cert_attachments")
        .filter((file) => file.get("file_id") !== action.payload);
      return state.set("cert_attachments", fromJS(files));
    case constants.CHANGE_FILE_LIST:
      return state.set(
        "uploadFiles",
        state.get("uploadFiles").concat(action.files),
      );
    case constants.INITIAL_FILE_LIST:
      if (action.fileList !== undefined && action.fileList) {
        return state.set("editFileList", fromJS(action.fileList));
      } else {
        return state.merge({
          editFileList: fromJS([]),
          uploadFiles: fromJS([]),
        });
      }
    case constants.CANCEL_ONE_FILE:
      return removeFileFromList(state, action);
    case constants.UPDATE_SUMMARY:
      return updateCertSummary(state, action);
    case constants.SET_PREVIOUS_CERTIFIED:
      return setPreviousCertified(state, action);
    case constants.SET_VARIATION_LINE_INFO:
      return setVariationLineItemInfo(state, action);
    case constants.HANDLE_LINE_TREE_EXPAND:
      return handleTreeExpand(state, action);
    default:
      return state;
  }
};
